import { Stack } from "@mui/material";
import { TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MfaCodeDescription,
  MfaCodeDigit,
  MfaCodeDigits,
  MfaCodeError,
  MfaCodeLink,
  MfaCodePanel,
  MfaCodeTitle
} from "./MfaCode.styles";
import { MFA_FACTOR } from "../../consts";
import { useAuthStore } from "../../stores";
import { isMfaFactor } from "../../utils/mfa";
import { MfaSelectButton } from "../MfaSelect/MfaSelect.styles";
import { Spinner } from "../Spinner";

const CODE_LENGTH = 6;

export function MfaCode() {
  const { t } = useTranslation();
  const challenge = useAuthStore(state => state.cognitoChallenge);
  const availableFactors = useAuthStore(state => state.mfaAvailableFactors);
  const isLoading = useAuthStore(state => state.isLoading);
  const agreedToConditions = useAuthStore(state => state.userInfo?.privacy_consent_flag === true);
  const respondToChallenge = useAuthStore(state => state.respondToChallenge);
  const resendMfaCode = useAuthStore(state => state.resendMfaCode);
  const switchMfaFactor = useAuthStore(state => state.switchMfaFactor);
  const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const [hasError, setHasError] = useState(false);
  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  const currentFactor = challenge?.challengeName;
  const isTotp = currentFactor === MFA_FACTOR.SOFTWARE_TOKEN_MFA;
  const alternativeFactor = isMfaFactor(currentFactor) && availableFactors.includes(currentFactor)
    ? availableFactors.find(factor => factor !== currentFactor)
    : undefined;
  const destination = challenge?.parameters.CODE_DELIVERY_DESTINATION ?? "";

  useEffect(() => {
    setCode(Array(CODE_LENGTH).fill(""));
    setHasError(false);
  }, [challenge?.session, currentFactor]);

  function updateCode(value: string, position: number) {
    const digits = value.replace(/\D/g, "").slice(0, CODE_LENGTH);
    if (value && !digits) return;

    setHasError(false);
    // Un codice completo incollato o compilato dal browser sostituisce tutte le cifre.
    const start = digits.length === CODE_LENGTH ? 0 : position;
    setCode(previous => {
      const next = [...previous];
      if (!digits) next[start] = "";
      for (let index = 0; index < digits.length && start + index < CODE_LENGTH; index++) {
        next[start + index] = digits[index];
      }
      return next;
    });
    if (digits) inputs.current[Math.min(start + digits.length, CODE_LENGTH - 1)]?.focus();
  }

  function verifyCode() {
    if (isLoading || !isMfaFactor(currentFactor)) return;
    const emptyPosition = code.findIndex(digit => !digit);
    if (emptyPosition !== -1) {
      inputs.current[emptyPosition]?.focus();
      return;
    }

    setHasError(false);
    respondToChallenge(code.join(""), {
      state: agreedToConditions ? {} : { first: true, isAuthenticated: true },
      target: agreedToConditions ? "/" : "/agreements"
    }, true, () => setHasError(true));
  }

  if (isLoading) return <Spinner />;

  return (
    <MfaCodePanel role="group" aria-labelledby="mfa-code-title">
      <Stack alignItems="center" gap={ 2 } width="100%">
        <MfaCodeTitle variant="h1" id="mfa-code-title">
          { t(isTotp ? "mfa_code_totp_title" : "mfa_code_email_title") }
        </MfaCodeTitle>
        <MfaCodeDescription id="mfa-code-description">
          { t(isTotp ? "mfa_code_totp_description" : "mfa_code_email_description") }
          { !isTotp && destination && <><br /><strong>{ destination }</strong></> }
        </MfaCodeDescription>
      </Stack>

      <MfaCodeDigits
        role="group"
        aria-label={ t(isTotp ? "enter_authenticator_code" : "enter_otp_code") }
        onKeyDown={ event => {
          if (event.key === "Enter") {
            event.preventDefault();
            verifyCode();
          }
        } }
      >
        { code.map((digit, position) => (
          <MfaCodeDigit
            aria-describedby={ hasError ? "mfa-code-description mfa-code-error" : "mfa-code-description" }
            aria-invalid={ hasError }
            aria-label={ t("otp_code", { position: position + 1, total: CODE_LENGTH }) }
            autoComplete={ position === 0 ? "one-time-code" : "off" }
            data-testid="login-otp-digit-input"
            id={ `login-otp-digit-${position + 1}-input` }
            inputMode="numeric"
            key={ position }
            onChange={ event => updateCode(event.target.value, position) }
            onFocus={ event => event.target.select() }
            onKeyDown={ event => {
              if (event.key === "Backspace" && !digit && position > 0) {
                event.preventDefault();
                updateCode("", position - 1);
                inputs.current[position - 1]?.focus();
              } else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
                event.preventDefault();
                const next = position + (event.key === "ArrowLeft" ? -1 : 1);
                inputs.current[Math.max(0, Math.min(next, CODE_LENGTH - 1))]?.focus();
              }
            } }
            onPaste={ event => {
              event.preventDefault();
              updateCode(event.clipboardData.getData("text"), position);
            } }
            pattern="[0-9]*"
            ref={ element => { inputs.current[position] = element } }
            type="text"
            value={ digit }
          />
        )) }
      </MfaCodeDigits>

      { hasError && <MfaCodeError id="mfa-code-error" severity="error" icon={ <TriangleAlert size={ 24 } /> }>
        { t(isTotp ? "err_msg_totp_remainder" : "mfa_code_email_error") }
      </MfaCodeError> }

      <Stack gap={ 2 } width="100%">
        <MfaSelectButton
          data-testid="confirm-otp-button"
          disabled={ !isMfaFactor(currentFactor) }
          id="confirm-otp-button"
          onClick={ verifyCode }
          type="button"
          variant="contained"
        >
          { t("mfa_code_verify") }
        </MfaSelectButton>
        { !isTotp && <MfaCodeLink onClick={ () => { resendMfaCode() } } type="button">
          { t("mfa_code_resend") }
        </MfaCodeLink> }
        { alternativeFactor && <MfaCodeLink
          onClick={ () => { switchMfaFactor(alternativeFactor) } }
          sx={ { marginTop: isTotp && hasError ? 0 : 2 } }
          type="button"
        >
          { t(isTotp ? "mfa_code_switch_email" : "mfa_code_switch_totp") }
        </MfaCodeLink> }
      </Stack>
    </MfaCodePanel>
  );
}
